import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { List, X, Warning } from "@phosphor-icons/react";
import { useAuth, isStaff } from "@/context/AuthContext";
import { RISK } from "@/lib/api";
import { PreviewBanner } from "@/components/PreviewBanner";

const LINKS = [
  ["/courses", "Courses"], ["/cities", "Cities"], ["/pricing", "Pricing"], ["/mission-control", "The Program"],
  ["/about", "Instructor"], ["/free-seminar", "Free Seminar"], ["/faq", "FAQ"],
];

export const Logo = ({ dark = true }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
    <img src="/brand/11_App_Icon_512x512.png" alt="Mission Control Trading logo" className="h-9 w-9 rounded-md" />
    <span className={`font-display text-xl font-extrabold uppercase leading-none tracking-wide ${dark ? "text-white" : "text-[#0B1B33]"}`}>
      Mission Control <span className="text-[#E0A21B]">Trading</span>
    </span>
  </Link>
);

const AccountLink = ({ onClick }) => {
  const { user } = useAuth();
  if (!user) return <Link to="/login" onClick={onClick} data-testid="nav-signin-link" className="btn-gold py-2 text-sm">Sign in</Link>;
  return (
    <Link to={isStaff(user) ? "/superadmin" : "/app"} onClick={onClick} data-testid="nav-account-link" className="btn-gold py-2 text-sm">
      {isStaff(user) ? "Superadmin" : "My App"}
    </Link>
  );
};

const Nav = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0B1B33]/90 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-6 lg:flex">
          {LINKS.map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) => `text-sm font-medium transition-colors duration-200 hover:text-[#E0A21B] ${isActive ? "text-[#E0A21B]" : "text-white/80"}`}>
              {label}
            </NavLink>
          ))}
          <AccountLink />
        </nav>
        <button className="text-white lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
          {open ? <X size={28} /> : <List size={28} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 bg-[#0B1B33] px-5 pb-5 lg:hidden" data-testid="nav-mobile-menu">
          {LINKS.map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)} className="block border-b border-white/5 py-3 text-white/85">{label}</Link>
          ))}
          <div className="pt-4"><AccountLink onClick={() => setOpen(false)} /></div>
        </div>
      )}
    </header>
  );
};

export const RiskNotice = ({ dark = false, className = "" }) => (
  <div data-testid="risk-notice" className={`flex items-start gap-2.5 text-xs leading-relaxed ${dark ? "text-white/60" : "text-[#475569]"} ${className}`}>
    <Warning size={16} weight="fill" className="mt-0.5 shrink-0 text-[#C8382E]" />
    <span>{RISK}</span>
  </div>
);

const COLS = [
  ["Learn", [["/courses", "Courses"], ["/mission-control", "The Program"], ["/quiz", "Trader-Type Quiz"], ["/free-seminar", "Free Seminar"]]],
  ["Tour", [["/cities", "Cities"], ["/leaderboard", "Leaderboard"], ["/gift-cards", "Gift Cards"], ["/pricing", "Pricing"], ["/about", "Instructor"], ["/faq", "FAQ"]]],
  ["Company", [["/contact", "Contact"], ["/verify", "Verify a Certificate"], ["/login", "Student Login"]]],
  ["Legal", [["/terms", "Terms"], ["/privacy", "Privacy"], ["/refund-policy", "Refund Policy"], ["/risk-disclosure", "Risk Disclosure"], ["/referral-terms", "Referral Terms"], ["/accessibility", "Accessibility"]]],
];

const Footer = () => (
  <footer className="relative bg-[#0B1B33] text-white noise">
    <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
      <div className="grid gap-10 md:grid-cols-12">
        <div className="md:col-span-4">
          <Logo />
          <p className="mt-4 max-w-xs text-sm text-white/60">Plan the Launch. Respect the Landing. A classroom multi-asset trading course touring Canada.</p>
          <p className="mt-3 text-xs text-white/40">ForeXperts AI Inc. · missioncontroltrading.ca</p>
        </div>
        {COLS.map(([title, links]) => (
          <div key={title} className="md:col-span-2">
            <div className="eyebrow mb-4">{title}</div>
            <ul className="space-y-2.5">
              {links.map(([to, l]) => (
                <li key={to}><Link to={to} className="text-sm text-white/70 transition-colors hover:text-[#E0A21B]">{l}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-12 border-t border-white/10 pt-6">
        <RiskNotice dark />
        <p className="mt-3 text-xs text-white/40">© {new Date().getFullYear()} ForeXperts AI Inc. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col">
      <PreviewBanner />
      <Nav />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
